/**
 * Settings Service
 *
 * Persists add-in settings inside the document file itself via the Office
 * Settings API (Office.context.document.settings). Unlike OfficeRuntime.storage,
 * these settings are saved into the document and travel with it wherever it's opened.
 */

/* global Office */

/** Document setting key under which the user's selected style profile name is stored. */
export const SELECTED_PROFILE_SETTING_KEY = "mss.selectedProfile";

/** Document setting key under which the review session's filters/position are stored. */
export const REVIEW_PROGRESS_SETTING_KEY = "mss.reviewProgress";

/**
 * Document setting key holding the outcome of the last styling run
 * (a `ClassificationOutcome`). Absent means no run has been recorded: never
 * run, cleared at the start of the current run, or styled by a build that
 * predates this key.
 */
export const LAST_STYLING_RUN_SETTING_KEY = "mss.lastStylingRun";

/**
 * Saves a setting into the document.
 *
 * Values are held in memory until saveAsync persists them to the document
 * file, so save immediately after setting to avoid losing the change.
 *
 * @param name - The setting's key
 * @param value - The value to store; must be JSON-serializable
 */
export async function saveDocumentSetting(name: string, value: unknown): Promise<void> {
  setDocumentSetting(name, value);
  return saveDocumentSettings();
}

/**
 * Sets a setting in memory only; it reaches the document file on the next
 * {@link saveDocumentSettings}. Reads via {@link loadDocumentSetting} see it immediately.
 */
export function setDocumentSetting(name: string, value: unknown): void {
  Office.context.document.settings.set(name, value);
}

/** Persists every in-memory setting to the document file. */
export async function saveDocumentSettings(): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        const err = new Error(`Failed to save document settings: ${result.error.message}`);
        console.error("[saveDocumentSettings]", err);
        reject(err);
        return;
      }
      resolve();
    });
  });
}

/**
 * Loads a setting from the document.
 *
 * Settings are loaded into memory when the add-in activates, so this reads
 * synchronously without needing a fresh call to the document file.
 *
 * @param name - The setting's key
 * @returns The stored value, or `null` if the setting has never been saved
 */
export function loadDocumentSetting<T>(name: string): T | null {
  const value = Office.context.document.settings.get(name);
  return value === undefined || value === null ? null : (value as T);
}

/**
 * Removes a setting from the document.
 *
 * @param name - The setting's key
 */
export async function removeDocumentSetting(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.settings.remove(name);
    Office.context.document.settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        const err = new Error(`Failed to remove setting "${name}": ${result.error.message}`);
        console.error("[removeDocumentSetting]", err);
        reject(err);
        return;
      }
      resolve();
    });
  });
}
